/**
 * 地图数据更新指令处理器
 * 负责解析后端返回的指令（如 EDIT_POINT）并执行对应的地图操作
 */

export const updateHandler = {
  /**
   * 处理点位更新 (EDIT_POINT)
   * @param {Object} vm - EditingAmap 组件实例 (this)
   * @param {Array} pointList - 需要更新的点位数据列表
   */
  processPointUpdate (vm, pointList) {
    if (!pointList || !Array.isArray(pointList)) return;

    pointList.forEach(pointItem => {
      const targetId = String(pointItem.id);
      let found = false;

      // 1. 移除旧要素 (跨所有点位图层查找)
      Object.keys(vm.markerSources).forEach(type => {
        const source = vm.markerSources[type];
        // 增强查找：尝试直接获取，或遍历查找属性匹配的要素
        let feature = source.getFeatureById(targetId) ||
          source.getFeatures().find(f => String(f.getId()) === targetId || String(f.get('id')) === targetId);

        if (feature) {
          source.removeFeature(feature);
          source.changed(); // 强制触发图层更新
          found = true;
          console.log(`[UpdateHandler] 已从图层 ${type} 移除旧点: ${targetId}`);
        }
      });

      // 2. 清理本地数据缓存，防止切换图层时旧数据重新加载
      Object.keys(vm.markerDataGroups).forEach(type => {
        const initialCount = vm.markerDataGroups[type].length;
        vm.markerDataGroups[type] = vm.markerDataGroups[type].filter(f => {
          const fid = String(f.getId() || f.get('id'));
          return fid !== targetId;
        });
        if (vm.markerDataGroups[type].length < initialCount) {
          console.log(`[UpdateHandler] 已从缓存 ${type} 中清理旧数据: ${targetId}`);
        }
      });

      // 3. 添加新要素
      vm.addSingleMarker(pointItem);

      if (!found) {
        console.warn(`[UpdateHandler] 未在地图图层中找到 ID 为 ${targetId} 的旧点`);
      }
    });

    vm.map.render(); // 强制地图渲染
  },

  /**
   * 处理线位更新 (EDIT_LINE)
   * @param {Object} vm - EditingAmap 组件实例
   * @param {Array} lineList - 需要更新的线位数据列表
   */
  processLineUpdate (vm, lineList) {
    if (!lineList || !vm.lineSource || !Array.isArray(lineList)) return;

    lineList.forEach(lineItem => {
      const targetId = String(lineItem.id);
      const source = vm.lineSource;

      // 增强查找
      const feature = source.getFeatureById(targetId) ||
        source.getFeatures().find(f => String(f.getId()) === targetId || String(f.get('id')) === targetId);

      if (feature) {
        source.removeFeature(feature);
        console.log(`[UpdateHandler] 已从图层移除旧线: ${targetId}`);
      }

      vm.addSingleLine(lineItem);
    });

    vm.lineSource.changed();
    vm.map.render();
  },

  /**
   * 处理点位删除 (DEL_POINT)
   * @param {Object} vm - EditingAmap 组件实例
   * @param {Array} pointList - 需要删除的点位数据列表
   */
  processPointDelete (vm, pointList) {
    if (!pointList || !Array.isArray(pointList)) return;

    pointList.forEach(pointItem => {
      const targetId = String(pointItem.id);

      // 1. 从所有点位图层中移除
      Object.keys(vm.markerSources).forEach(type => {
        const source = vm.markerSources[type];
        const feature = source.getFeatureById(targetId) ||
          source.getFeatures().find(f => String(f.getId()) === targetId || String(f.get('id')) === targetId);

        if (feature) {
          source.removeFeature(feature);
          source.changed();
          console.log(`[UpdateHandler] 已从图层 ${type} 移除点: ${targetId}`);
        }
      });

      // 2. 从本地数据缓存中清理
      Object.keys(vm.markerDataGroups).forEach(type => {
        vm.markerDataGroups[type] = vm.markerDataGroups[type].filter(f => {
          const fid = String(f.getId() || f.get('id'));
          return fid !== targetId;
        });
      });
    });

    vm.map.render();
  },

  /**
   * 处理线位删除 (DEL_LINE)
   * @param {Object} vm - EditingAmap 组件实例
   * @param {Array} lineList - 需要删除的线位数据列表
   */
  processLineDelete (vm, lineList) {
    if (!lineList || !vm.lineSource || !Array.isArray(lineList)) return;

    lineList.forEach(lineItem => {
      const targetId = String(lineItem.id);
      const source = vm.lineSource;

      const feature = source.getFeatureById(targetId) ||
        source.getFeatures().find(f => String(f.getId()) === targetId || String(f.get('id')) === targetId);

      if (feature) {
        source.removeFeature(feature);
        console.log(`[UpdateHandler] 已从图层移除线: ${targetId}`);
      }
    });

    vm.lineSource.changed();
    vm.map.render();
  }
};

/**
 * 指令策略映射表
 */
export const strategyMap = {
  'EDIT_POINT': (vm, resData) => updateHandler.processPointUpdate(vm, resData.pointList),
  'EDIT_LINE': (vm, resData) => updateHandler.processLineUpdate(vm, resData.lineList),
  'DEL_POINT': (vm, resData) => updateHandler.processPointDelete(vm, resData.pointList),
  'DEL_LINE': (vm, resData) => updateHandler.processLineDelete(vm, resData.lineList),
  'ADD_POINT': (vm, resData) => updateHandler.processPointUpdate(vm, resData.pointList), // 新增点也可以复用更新逻辑（先试着移除，然后添加）
  'ADD_LINE': (vm, resData) => updateHandler.processLineUpdate(vm, resData.lineList),
};
